"use client";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Database } from "@masterkey/supabase/types";
import { Mail } from "lucide-react";

type UserWithRelations = Database["public"]["Tables"]["users"]["Row"] & {
  subscription: Database["public"]["Tables"]["subscriptions"]["Row"];
  journey: Database["public"]["Tables"]["home_buying_journeys"]["Row"] | null;
  documents: Database["public"]["Tables"]["documents"]["Row"][];
  savedProperties: Database["public"]["Tables"]["journey_saved_properties"]["Row"][];
  analyses: (Database["public"]["Tables"]["analyzed_properties"]["Row"] & {
    property: Database["public"]["Tables"]["properties"]["Row"];
  })[];
  recentEvents: Database["public"]["Tables"]["events"]["Row"][];
  recentTasks: Database["public"]["Tables"]["tasks"]["Row"][];
};

interface WelcomeEmailButtonProps {
  user: UserWithRelations;
  variant?: "default" | "table";
}

export function WelcomeEmailButton({
  user,
  variant = "default",
}: WelcomeEmailButtonProps) {
  const handleSendWelcomeEmail = () => {
    const emailBody = `Hey ${user.first_name},

Saw you tried our AI property analyzer. Two quick questions:

- What was particularly helpful or missing from your analysis?
- Besides crazy prices (wish I could fix those!), what's your biggest headache in your home search right now?

Reply with just 1-2 sentences and I'll add 2 bonus analyses to your account as a quick thank you. Even the shortest feedback shapes what we build next.

Cheers,
Giulio
Founder, Masterkey
https://trymasterkey.com`;

    const mailtoUrl = `mailto:${user.email}?subject=${encodeURIComponent(
      `${user.first_name}- 2 free analyses for quick feedback on Masterkey`,
    )}&body=${encodeURIComponent(emailBody)}`;

    window.open(mailtoUrl, "_blank");
  };

  if (variant === "table") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-green-600 hover:text-green-700"
              onClick={handleSendWelcomeEmail}
            >
              <Mail className="h-4 w-4" />
              <span className="sr-only">Send Welcome Email</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Send Welcome Email</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="flex items-center gap-2"
      onClick={handleSendWelcomeEmail}
    >
      <Mail className="h-4 w-4" />
      Send Welcome Email
    </Button>
  );
}
